import { useMemo } from 'react';

import { QueryResult, gql } from '@apollo/client';

import {
  CandidateAvailabilityTokensQuery,
  RenderType,
  TemplateToken,
  TokenCandidateAvailabilityInput,
  useCandidateAvailabilityTokensQuery,
} from 'src/generated/mloop-graphql';

import { EMPTY_OBJECT } from 'src/constants';

import placeholders from './utils/placeholders';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CandidateAvailabilitylaceholderFillerQuery = gql`
  query CandidateAvailabilityTokens(
    $id: String
    $renderType: RenderType!
    $candidateAvailabilityInput: TokenCandidateAvailabilityInput!
    $timezone: timezone!
  ) {
    CANDIDATE_AVAILABILITY: templateToken(input: { id: $id, renderType: $renderType }) {
      id
      CANDIDATE_AVAILABILITY(input: $candidateAvailabilityInput)
    }
    CANDIDATE_AVAILABILITY_TIMEZONE: templateToken(input: { id: $id, renderType: $renderType }) {
      id
      CANDIDATE_AVAILABILITY_TIMEZONE(input: { timezone: $timezone })
    }
  }
`;

/**
 * TOKENS FETCHED
 */
export enum Tokens {
  CANDIDATE_AVAILABILITY = 'CANDIDATE_AVAILABILITY',
  CANDIDATE_AVAILABILITY_TIMEZONE = 'CANDIDATE_AVAILABILITY_TIMEZONE',
  CANDIDATE_AVAILABILITY_URL = 'CANDIDATE_AVAILABILITY_URL',
}
export type CandidateAvailabilityPlaceholderFillerOptions = Pick<TemplateToken, keyof typeof Tokens>;

export default class CandidateAvailabilityPlaceholderFiller {
  static getFilledText = (input: string, options: CandidateAvailabilityPlaceholderFillerOptions): string => {
    return placeholders(input, options);
  };

  static getOptions = (data?: CandidateAvailabilityTokensQuery): CandidateAvailabilityPlaceholderFillerOptions => {
    if (!data || !Object.keys(data).length) {
      return EMPTY_OBJECT;
    }
    return {
      CANDIDATE_AVAILABILITY: data.CANDIDATE_AVAILABILITY?.CANDIDATE_AVAILABILITY,
      CANDIDATE_AVAILABILITY_TIMEZONE: data.CANDIDATE_AVAILABILITY_TIMEZONE?.CANDIDATE_AVAILABILITY_TIMEZONE,
    };
  };
}

export type Input = {
  renderType?: RenderType;
  applicationId: string;
  timezone: string;
  candidateAvailabilityInput?: TokenCandidateAvailabilityInput;
  isFetch?: boolean;
};

export const useCandidateAvailabilityPlaceholderFillerOptions = ({
  renderType,
  applicationId,
  timezone,
  candidateAvailabilityInput,
  isFetch = true,
}: Input): [CandidateAvailabilityPlaceholderFillerOptions, Pick<QueryResult, 'data' | 'error' | 'loading'>] => {
  const { data, loading, error } = useCandidateAvailabilityTokensQuery({
    skip: !isFetch || !applicationId,
    variables: {
      renderType: renderType || RenderType.Html,
      id: applicationId,
      timezone,
      candidateAvailabilityInput: candidateAvailabilityInput ?? {
        timezone,
        timeRanges: [],
      },
    },
    errorPolicy: 'all',
  });

  return useMemo(() => {
    return [CandidateAvailabilityPlaceholderFiller.getOptions(data), { data, error, loading }];
  }, [data, error, loading]);
};
